import ClearIcon from '@mui/icons-material/Clear';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import FileOpenIcon from '@mui/icons-material/FileOpen';
import HelpCenterOutlinedIcon from '@mui/icons-material/HelpCenterOutlined';
import SmsFailedIcon from '@mui/icons-material/SmsFailed';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { useState } from 'react';

import { fileToBase64 } from '#components/simple-file-uploader/file-to-base64.js';
import { SimpleFileUploader } from '#components/simple-file-uploader/simple-file-uploader.js';
import { useTRPC } from '#src/trpc.js';
import { fileContentsQueryKey } from '#utils/file-contents-query.js';
import { useDataElTagGenerator } from '#utils/use-data-el-tag-generator.js';
import { describeDatasheetSearch, ReplaceDatasheetReason } from './replace-datasheet-dialog-utils.js';

import type { AppRouter } from '@cadstrom/backend/trpc';
import type SvgIcon from '@mui/material/SvgIcon';
import type { inferRouterOutputs } from '@trpc/server';
import type { DatasheetSearchMessage } from './replace-datasheet-dialog-utils.js';

/** A package as the server returns it after its datasheet changed. */
type ChangedPackage = inferRouterOutputs<AppRouter>['package']['setDatasheet']['pkg'];

export type ReplaceDatasheetDialogProps = {
	pkgId: string;
	reason: ReplaceDatasheetReason;
	showCloseButton?: boolean;
	onClose?: () => void;
	onReplaceSuccess?: () => void;
};

/** The visual tone of the dialog, which several reasons may share. */
enum ReplaceDatasheetSeverity {
	Neutral = 'neutral',
	Error = 'error',
}

/** Class names for the parts of the dialog that change with its severity. */
interface ReplaceDatasheetSeverityStyle {
	/** Colour of the title and the header icon. */
	titleColor: string;
	subtitleColor: string;
	/** Border, background and shadow of the outer panel. */
	bgClassNames: string;
	/** Background of the inner panel that holds the upload and resolve forms. */
	subpanelBgClassNames: string;
}

const SEVERITY_STYLES: Record<ReplaceDatasheetSeverity, ReplaceDatasheetSeverityStyle> = {
	[ReplaceDatasheetSeverity.Neutral]: {
		titleColor: clsx('text-blue-a12'),
		subtitleColor: clsx('text-gray-a10'),
		bgClassNames: clsx('border-blue-a3 bg-blue-2/80 shadow-blue-10/10 light:shadow-blue-5/40'),
		subpanelBgClassNames: clsx('bg-blue-a2 light:bg-gray-1/95'),
	},
	[ReplaceDatasheetSeverity.Error]: {
		titleColor: clsx('text-red-12'),
		subtitleColor: clsx('text-red-12/50'),
		bgClassNames: clsx('border-red-a3 bg-red-3 shadow-red-6/30 light:shadow-red-5/40'),
		subpanelBgClassNames: clsx('bg-gray-1/50 light:bg-gray-1/80'),
	},
};

/** What the dialog says and shows for one reason it was opened. */
interface ReplaceDatasheetReasonDisplay {
	title: string;
	subtitle: string | null;
	Icon: typeof SvgIcon;
	severity: ReplaceDatasheetSeverity;
	/** Whether to offer retrying the original load instead of replacing the datasheet. */
	allowRetry: boolean;
}

const REASON_DISPLAY: Record<ReplaceDatasheetReason, ReplaceDatasheetReasonDisplay> = {
	[ReplaceDatasheetReason.Replacing]: {
		title: 'Replace datasheet',
		subtitle: null,
		Icon: FileOpenIcon,
		severity: ReplaceDatasheetSeverity.Neutral,
		allowRetry: false,
	},
	[ReplaceDatasheetReason.ExplicitlyEmpty]: {
		title: 'No datasheet set.',
		subtitle: 'This package does not have a datasheet yet. You can set one below.',
		Icon: HelpCenterOutlinedIcon,
		severity: ReplaceDatasheetSeverity.Neutral,
		allowRetry: false,
	},
	[ReplaceDatasheetReason.ErrorRetrievingFile]: {
		title: 'Error retrieving datasheet.',
		subtitle: 'The file may be missing. You can try replacing it with a new file below.',
		Icon: SmsFailedIcon,
		severity: ReplaceDatasheetSeverity.Error,
		allowRetry: true,
	},
	[ReplaceDatasheetReason.InvalidPdf]: {
		title: 'This datasheet is not a valid PDF.',
		subtitle: 'The stored file could not be read as a PDF. You can replace it with a new file below.',
		Icon: SmsFailedIcon,
		severity: ReplaceDatasheetSeverity.Error,
		allowRetry: false,
	},
};

export function ReplaceDatasheetDialog({
	pkgId,
	reason,
	showCloseButton = false,
	onClose,
	onReplaceSuccess,
}: ReplaceDatasheetDialogProps) {
	const queryClient = useQueryClient();
	const trpc = useTRPC();

	const [searchMessage, setSearchMessage] = useState<DatasheetSearchMessage | null>(null);

	/**
	 * Shows the package the server returned after its datasheet changed, dropping what was read from the old one.
	 *
	 * @param pkg - The package as it is now.
	 */
	const applyChangedPackage = (pkg: ChangedPackage) => {
		// Merge rather than replace: `isConfidential` is derived from the package family's owner and
		// is not in the mutation response, so overwriting the entry would drop it and hide the badge.
		queryClient.setQueryData(trpc.package.get.queryOptions({ id: pkgId }).queryKey, (previous) =>
			previous === undefined ? undefined : { ...previous, pkg },
		);
		if (pkg.fileId !== null) {
			void queryClient.resetQueries({ queryKey: fileContentsQueryKey(pkg.fileId) });
		}
		// Row evidence is keyed by package alone, so lists read from the old datasheet would otherwise survive.
		void queryClient.invalidateQueries({
			queryKey: trpc.packageEvidence.rowEvidence.queryKey({ packageId: pkgId }),
		});
		onReplaceSuccess?.();
	};

	const { mutateAsync: setPackageDatasheet, isPending: setPackageDatasheetIsPending } = useMutation(
		trpc.package.setDatasheet.mutationOptions({
			onSuccess: (res) => {
				setSearchMessage(null);
				applyChangedPackage(res.pkg);
			},
		}),
	);

	const { mutate: findPackageDatasheet, isPending: findPackageDatasheetIsPending } = useMutation(
		trpc.package.findDatasheet.mutationOptions({
			onSuccess: (res) => {
				setSearchMessage(describeDatasheetSearch(res));
				const shownFileId =
					queryClient.getQueryData(trpc.package.get.queryOptions({ id: pkgId }).queryKey)?.pkg.fileId ?? null;
				// Another writer may have given the package a datasheet while the search ran.
				if (res.pkg.fileId !== shownFileId) {
					applyChangedPackage(res.pkg);
				}
			},
			onError: () => {
				setSearchMessage(describeDatasheetSearch({ running: false, outcome: null, reason: null, nextAttemptAt: null }));
			},
		}),
	);
	const isPending = setPackageDatasheetIsPending || findPackageDatasheetIsPending;

	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [directUploadSourceUrlInputValue, setDirectUploadSourceUrlInputValue] = useState('');
	const [linkToPdfInputValue, setLinkToPdfInputValue] = useState('');

	const handleUploadFile = () => {
		if (!selectedFile) {
			return;
		}

		void fileToBase64(selectedFile).then(async (fileBase64) => {
			await setPackageDatasheet({
				pkgId,
				fileBase64,
				fileName: selectedFile.name,
				sourceUrl: directUploadSourceUrlInputValue,
			});
		});
	};

	// A search never replaces a datasheet, so a package that holds one needs a URL to replace it.
	const packageHoldsFile = reason !== ReplaceDatasheetReason.ExplicitlyEmpty;
	const trimmedLinkToPdf = linkToPdfInputValue.trim();

	const handleRequestDatasheet = () => {
		setSearchMessage(null);
		if (trimmedLinkToPdf === '') {
			findPackageDatasheet({ pkgId });
			return;
		}
		void setPackageDatasheet({ pkgId, sourceUrl: trimmedLinkToPdf });
	};

	const { title, subtitle, Icon: HeaderIconComponent, severity, allowRetry } = REASON_DISPLAY[reason];
	const { titleColor, subtitleColor, bgClassNames, subpanelBgClassNames } = SEVERITY_STYLES[severity];

	const el = useDataElTagGenerator(ReplaceDatasheetDialog);
	return (
		<div
			{...el()}
			className={clsx(
				'flex min-h-0 min-w-0 grow flex-col gap-2 rounded-lg border p-2 shadow-[0px_6px_16px_var(--tw-shadow-color)] backdrop-blur-lg',
				bgClassNames,
			)}
		>
			<div className={clsx('flex items-center gap-4 p-2')}>
				<HeaderIconComponent className={clsx('text-3xl', titleColor)} />

				<div className='flex grow flex-col gap-2'>
					<p className={clsx('text-lg font-medium', titleColor)}>{title}</p>
					{subtitle !== null && <p className={subtitleColor}>{subtitle}</p>}
				</div>

				{allowRetry && onReplaceSuccess !== undefined && (
					<Button variant='outlined' onClick={onReplaceSuccess}>
						<p>Retry</p>
					</Button>
				)}
				{showCloseButton && (
					<IconButton className='ml-auto' onClick={onClose}>
						<CloseIcon />
					</IconButton>
				)}
			</div>

			<div
				className={clsx(
					'@container relative min-h-0 overflow-auto rounded-lg px-4 py-5 contain-[paint]',
					subpanelBgClassNames,
				)}
			>
				<div
					className={clsx(
						'grid grid-cols-1 gap-6 @2xl:grid-cols-[1fr_auto_1fr]',
						isPending && 'pointer-events-none opacity-50 contrast-50',
					)}
				>
					<div className='flex flex-col gap-2'>
						<p className='text-2xl font-semibold text-gray-a11'>Upload a datasheet</p>

						<div className='m-2 flex flex-col gap-2'>
							<SimpleFileUploader maxFileSize={60 * 1024 * 1024} onChange={setSelectedFile} />

							<div className='mx-1 flex items-center gap-2'>
								<p className='font-medium text-gray-a11'>Selected file:</p>

								{selectedFile ?
									<>
										<div className='flex h-0 min-w-0 grow items-center gap-1'>
											<DescriptionIcon />
											<p className='truncate grow py-0.5 contain-[inline-size]'>{selectedFile.name}</p>
										</div>

										<IconButton color='error' className='h-0' onClick={() => setSelectedFile(null)}>
											<ClearIcon />
										</IconButton>
									</>
								:	<p className='text-gray-a8'>&mdash;</p>}
							</div>
						</div>

						<TextField
							label='Source URL'
							variant='outlined'
							required
							value={directUploadSourceUrlInputValue}
							onChange={(event) => setDirectUploadSourceUrlInputValue(event.target.value)}
						/>

						<Button disabled={!selectedFile || directUploadSourceUrlInputValue === ''} onClick={handleUploadFile}>
							<p>Upload file</p>
						</Button>
					</div>

					{/* The rule turns with the layout: a horizontal break between the stacked halves, a vertical
					    one between the side-by-side columns. Either way the label sits in the gap in the rule
					    and overflows the 2px thickness, centred on it. */}
					<div className='grid h-0.5 w-full grid-cols-[1fr_auto_1fr] self-center @2xl:h-[calc(100%+1.25rem)] @2xl:w-0.5 @2xl:grid-cols-none @2xl:grid-rows-[1fr_auto_1fr]'>
						<div className='bg-gray-a3' />
						<div className='flex min-w-0 items-center justify-center px-2 @2xl:w-full @2xl:px-0 @2xl:py-1'>
							<span className='text-sm font-bold text-gray-a9'>OR</span>
						</div>
						<div className='bg-gray-a3' />
					</div>

					<div className='flex flex-col gap-4'>
						<p className='text-2xl font-semibold text-gray-a11'>Auto-resolve datasheet</p>

						<div className='flex grow flex-col justify-end gap-4'>
							<div className='flex flex-col gap-1.5'>
								<TextField
									label={packageHoldsFile ? 'Datasheet URL to download' : 'Datasheet URL to download (optional)'}
									variant='outlined'
									required={packageHoldsFile}
									slotProps={{ inputLabel: { shrink: true } }}
									className='mt-1.5'
									value={linkToPdfInputValue}
									onChange={(event) => setLinkToPdfInputValue(event.target.value)}
								/>
								<p className='mx-1 text-sm text-gray-a9'>
									{packageHoldsFile ?
										'The file at this URL replaces the package’s current datasheet.'
									:	'If not provided, the package’s catalogue links and part number are searched for a datasheet.'}
								</p>
							</div>

							<Button
								variant='outlined'
								disabled={packageHoldsFile && trimmedLinkToPdf === ''}
								onClick={handleRequestDatasheet}
							>
								<p>{packageHoldsFile ? 'Download and replace datasheet' : 'Attempt resolve datasheet'}</p>
							</Button>

							{searchMessage !== null && <Alert severity={searchMessage.severity}>{searchMessage.text}</Alert>}
						</div>
					</div>
				</div>

				{isPending && <CircularProgress className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2' />}
			</div>
		</div>
	);
}
